import type { IFilters, OrderBy } from '@/types/shared/filters'
import type { PolicyStatus } from './policies'

export interface IClaimHistoryFilters extends IFilters {
  start_date?: string
  end_date?: string
}

export interface IClaimHistory {
  id: number
  claim_id: number
  action: string
  action_user: string
  action_timestamp: string
}

export interface IClaimCosts {
  total: number
  details: {
    id: number
    charge: string
    cost: number
    payable_from: string
    payable_to: string
  }[]
}

/**
 * Represents a single failed part within a claim.
 *
 * @interface IClaimFailure
 * @property {string} part - The name of the part that has failed.
 * @property {string} part_sn - The serial number of the part that has failed.
 * @property {string} part_model - The model of the part that has failed.
 * @property {number} part_number - The number of the part that has failed.
 * @property {string} reason - A description or reason for the failure.
 */
export interface IClaimFailure {
  part: string
  part_sn?: string
  part_model?: string
  part_number?: number
  reason: string
}

/**
 * Displays summarized information about a claim for dashboard purposes.
 *
 * @interface IClaimDashboard
 * @property {number} id - The unique identifier for the claim.
 * @property {string} number - The claim number (e.g., displayed to users).
 * @property {{ model: string; sn: string }} miner - Basic miner info (model and serial number).
 * @property {string} policy_number - The policy number associated with this claim.
 * @property {number} failures - The total number of failed parts reported in this claim.
 * @property {string} status - The status of the claim (e.g., open, closed).
 * @property {string|null} repair_status - The current repair status, if any.
 * @property {string|null} repair_request_number - The associated repair request number, if any.
 * @property {list|null} costs - The costs associated with repairing or handling the claim.
 * @property {string|null} reject_reason - The reason if the claim was rejected.
 * @property {string|null} notes - Any additional information or notes.
 * @property {string} date_of_loss - The date of loss of the claim.
 * @property {string} created_at - The timestamp when the claim was originally created.
 * @property {string} updated_at - The timestamp of the most recent update to the claim.
 */
export interface IClaimDashboard {
  id: number
  number: string
  miner: {
    model_family: string
    sn: string
  }
  policy_number: string
  failures: number
  status: string
  repair_status: string | null
  work_order_number: string | null
  costs: IClaimCosts | null
  reject_reason: string | null
  notes: string | null
  date_of_loss: string
  created_at: string
  updated_at: string
}

/**
 * Displays detailed information about a claim, extending the dashboard view.
 *
 * @interface IClaimDetails
 * @extends IClaimDashboard
 * @property {string} customer - The customer name associated with this claim.
 * @property {number} policy_id - The policy ID that references this coverage.
 * @property {IClaimFailure[]} failures - The list of parts that failed in this claim.
 * @property {Array<{ id: number; picture_url: string; name?: string; uid?: number; status?: string }>} [pictures] -
 * @property {IClaimContainer} container_data - Claim container data
 *   An optional set of pictures (e.g., attachments) related to the claim.
 */
export interface IClaimContainer {
  type: string
  length: number
  width: number
  height: number
  weight: number
}

export interface IClaimDetails extends Omit<IClaimDashboard, 'failures'> {
  customer: string
  policy_id: number
  policy: {
    number: string
    group: string
    miner_sn: string
    sku: string
    location: string
    status: PolicyStatus
  }
  failures: IClaimFailure[]
  pictures?: {
    id: number
    picture_url: string
    name?: string
  }[]
  log_files?: {
    id: number
    name: string
    file_url: string
  }[]
  container_data: IClaimContainer | null
}

/**
 * Represents the filters that can be applied when fetching a list of claims.
 *
 * @interface IClaimFilters
 * @property {number} page - The page of results to request.
 * @property {number} per_page - The number of results per page.
 * @property {string} [order_by] - The field by which to order the results.
 * @property {OrderBy} [order_by_order] - Ascending or descending sort.
 * @property {number} [customer_id] - Optional filter by customer ID.
 * @property {string} [status] - Optional filter by claim status.
 * @property {string} [fragment] - Optional fragment search (e.g., partial match).
 */
export interface IClaimFilters {
  page: number
  per_page: number
  order_by?: string
  order_by_order?: OrderBy
  customer_id?: number
  status?: string
  has_costs?: boolean
  fragment?: string
}

/**
 * Displays possible claim statuses and associated reject reasons.
 *
 * @interface IClaimStatusesResponse
 * @property {string[]} claim_statuses - A list of all valid claim statuses.
 * @property {string[]} reject_reasons - A list of possible reasons for rejection.
 */
export interface IClaimStatusesResponse {
  claim_statuses: string[]
  reject_reasons: string[]
}

/**
 * The structure needed to update a claim status.
 *
 * @interface IClaimStatusUpdate
 * @property {string} status - The new status to set for the claim.
 * @property {string} [reject_reason] - The reason in the event of rejection.
 */
export interface IClaimStatusUpdate {
  status: string
  reject_reason?: string
}

/**
 * Enum of possible failing parts in a claim.
 *
 * @enum {string}
 */
export enum ClaimFailurePart {
  Hashboard = 'Hashboard',
  ControlBoard = 'Control Board',
  PowerSupply = 'Power Supply',
  Fan = 'Fan',
}

/**
 * Represents the shape required to create a claim, which can include pictures.
 *
 * @interface CreateClaimInput
 * @property {number} policy_id - The policy ID under which the claim is made.
 * @property {IClaimFailure[]} failures - The list of failed parts.
 * @property {string} [notes] - Optional notes for the claim.
 * @property {File[]} [pictures] - Optional array of images/files related to the claim.
 * @property {IClaimContainer} container - Container dimensions
 */
export interface CreateClaimInput {
  policy_id: number
  date_of_loss: string
  failures: IClaimFailure[]
  notes?: string
  pictures?: File[]
  log_files?: File[]
  container?: IClaimContainer
}

/**
 * Represents the shape needed to update an existing claim.
 *
 * @interface UpdateClaimInput
 * @property {IClaimFailure[]} failures - The updated list of failed parts.
 * @property {string} [notes] - Optional notes to update for the claim.
 * @property {File[]} [newPictures] - New images to add to the claim.
 * @property {number[]} [deletedPictureIds] - IDs of pictures to remove.
 * @property {IClaimContainer} container - Container dimensions
 * @property {string} [created_at] - Optional claim creation date for updates
 */
export interface UpdateClaimInput {
  failures: IClaimFailure[]
  notes?: string
  newPictures?: File[]
  deletedPictureIds?: number[]
  newLogFiles?: File[]
  deletedLogFileIds?: number[]
  container?: IClaimContainer
  created_at?: string
  date_of_loss?: string
}

/**
 * Extended FormData type for creating claims, providing stronger types and keys.
 */
export type ClaimFormData = FormData & {
  get(key: `policy_claims[${number}]policy_id`): string
  get(key: `policy_claims[${number}]failures[${number}]part`): `${ClaimFailurePart}`
  get(key: `policy_claims[${number}]failures[${number}]reason`): string
  get(key: `policy_claims[${number}]notes`): string | null

  append(key: `policy_claims[${number}]policy_id`, value: string): void
  append(key: `policy_claims[${number}]failures[${number}]part`, value: `${ClaimFailurePart}`): void
  append(key: `policy_claims[${number}]failures[${number}]reason`, value: string): void
  append(key: `policy_claims[${number}]notes`, value: string): void
}

/**
 * Extended FormData type for updating existing claims, providing stronger types and keys.
 */
export type UpdateClaimFormData = FormData & {
  get(key: `failures[${number}]part`): `${ClaimFailurePart}`
  get(key: `failures[${number}]reason`): string
  get(key: `deleted_pictures[${number}]`): string
  get(key: `new_pictures[${number}]`): File
  get(key: 'notes'): string | null
  get(key: 'created_at'): string | null

  append(key: `failures[${number}]part`, value: `${ClaimFailurePart}`): void
  append(key: `failures[${number}]reason`, value: string): void
  append(key: `deleted_pictures[${number}]`, value: string): void
  append(key: `new_pictures[${number}]`, value: File): void
  append(key: 'notes', value: string): void
  append(key: 'created_at', value: string): void
}

/**
 * Represents a writable form state (for ex. in a UI form),
 * containing the failures, notes, new images, and picture IDs to delete.
 *
 * @interface IClaimForm
 * @property {IClaimFailure[]} failures - List of parts that have failed.
 * @property {string} notes - Additional notes or details about the claim.
 * @property {File[]} newImages - Files that should be added to the claim.
 * @property {number[]} deletedPictureIds - A list of IDs referencing pictures to remove.
 * @property {IClaimContainer} container - Container dimensions
 * @property {string} [created_at] - Optional claim creation date for updates
 */
export interface IClaimForm {
  failures: IClaimFailure[]
  notes: string
  newImages: File[]
  deletedPictureIds: number[]
  newLogFiles: File[]
  deletedLogFileIds: number[]
  failed_parts: Record<
    ClaimFailurePart,
    { enabled: boolean; reason: string; part_sn?: string; part_model?: string; part_number?: number }
  >
  container?: IClaimContainer
  created_at?: string
  date_of_loss?: string
}

export interface IClaimCostsForm {
  costs: {
    key: any
    charge: string
    cost: number
    payable_from: string
    payable_to: string
  }[]
}

/**
 * Existent in ClaimCreateForm during claim creation
 */
export interface IClaimSubmission {
  customer: {
    id: number
    name: string
  }
  model_family: string
  serial_number: string
  policy_id: number
  date_of_loss: string
  failed_parts: IFailedParts
  note: string
  pictures: File[]
  log_files: File[]
  container?: {
    type: string
    length: number
    width: number
    height: number
    weight: number
  }
}

/**
 * Each of the selected parts flagged failing in ClaimCreateForm
 */
export interface IFailedPart {
  enabled: boolean
  part_sn?: string
  part_model?: string
  part_number?: number
  reason: string
}

/**
 * Record of selected parts within a Policy Claim's miner
 */
export type IFailedParts = Record<ClaimFailurePart, IFailedPart>

export enum ClaimStatusTypes {
  ClaimStatus = 'ClaimStatus',
  RepairStatus = 'RepairStatus',
}

export enum ClaimCostCharges {
  Parts = 'Parts',
  Deductible = 'Deductible',
  Labor = 'Labor',
  Repair = 'Repair',
  Replacement = 'Replacement',
  InboundShipping = 'Inbound Shipping',
  OutboundShipping = 'Outbound Shipping',
}

export enum ClaimCostPayableTo {
  Administrator = 'Administrator',
  Servicer = 'Servicer',
  Customer = 'Customer',
}

export enum ClaimStatus {
  UnderReview = 'Under Review',
  ConditionalApproval = 'Conditional Approval',
  PendingCustomerAuthorization = 'Pending Customer Authorization',
  Authorized = 'Authorized',
  Approved = 'Approved',
  Denied = 'Denied',
  Completed = 'Completed',
  Invoiced = 'Invoiced',
}
